/** @jsxRuntime classic */
/** @jsx jsx */
import * as React from 'react';
import { Link } from 'react-router';
import styled from '@emotion/styled';
import { css, jsx } from '@emotion/core';
import { COLOR, FONT_SIZE, FONT_WEIGHT } from '@ordermentum/ui-constants';

const ButtonStyles = css`
  appearance: none;
  border: none;
  font-size: ${FONT_SIZE.SM};
  font-weight: ${FONT_WEIGHT.LIGHT};
  padding: 0.5rem 0;
  text-align: center;
  background: transparent;

  & [data-id='icon'] span::before {
    color: ${COLOR.BRAND_500} !important;
  }

  & [data-name='icon-wrapper'] {
    color: ${COLOR.BRAND_500};
  }

  & [data-id='icon'] {
    margin-right: 0.5rem;
  }
`;

type Props = {
  className?: string;
  to?: string;
  href?: string;
  target?: string;
  download?: string | boolean;
  color?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  isInactive?: boolean;
  hasUnderline?: boolean;
  hover?: boolean;
};

const Text = styled('span')<Partial<Props>>`
  align-items: center;
  color: ${p => (p.color === 'orange' ? COLOR.ORANGE : COLOR.GRAY_500)};
  display: flex;
  font-weight: ${FONT_WEIGHT.NORMAL};
  text-decoration: ${p => (p.hasUnderline ? 'underline' : 'none')};
  :hover {
    text-decoration: ${p => (p.hover ? 'underline' : 'none')};
  }
`;

/**
 * @deprecated Use @ordermentum/ui-shared/components/button
 */
const TextButton = ({
  to,
  className = '',
  color = 'gray',
  href,
  target = '',
  download = '',
  onClick,
  children,
  isInactive,
  hasUnderline = false,
  hover = false,
}: Props) => {
  if (onClick && to) {
    return (
      <Link to={to} onClick={onClick} css={ButtonStyles}>
        <Text hover={hover} color={color}>
          {children}
        </Text>
      </Link>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        css={css(ButtonStyles)}
        className={className}
        disabled={isInactive}
      >
        <Text color={color} hover={hover} hasUnderline={hasUnderline}>
          {children}
        </Text>
      </button>
    );
  }

  if (href) {
    return (
      <a href={href} css={ButtonStyles} download={download} target={target}>
        <Text hover={hover} color={color} hasUnderline={hasUnderline}>
          {children}
        </Text>
      </a>
    );
  }

  return (
    <Link to={to || ''} css={ButtonStyles}>
      <Text hover={hover} color={color}>
        {children}
      </Text>
    </Link>
  );
};

export default TextButton;
